import React, { useState, useEffect, useCallback } from 'react';
import {
  getTeams, createTeam, updateTeam, deleteTeam,
  getBrands, createBrand, updateBrand, deleteBrand,
  getDomains, createDomain, deleteDomain, getUsers, changeUserRole,
  getNotifications, createNotification, updateNotification, deleteNotification,
  getFeatureRequests, updateFeatureRequestStatus, deleteFeatureRequest
} from '@/lib/api';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  Pencil, Trash, Plus, Users, Tag, Globe, UserList, ShieldCheck, User,
  Bell, Lightbulb, Clock, CheckCircle, XCircle
} from '@phosphor-icons/react';

const tabs = [
  { key: 'teams', label: 'Teams', icon: Users },
  { key: 'brands', label: 'Brands', icon: Tag },
  { key: 'domains', label: 'Domains', icon: Globe },
  { key: 'users', label: 'Users & Roles', icon: UserList },
  { key: 'notifications', label: 'Notifications', icon: Bell },
  { key: 'requests', label: 'Feature Requests', icon: Lightbulb },
];

const FR_STATUSES = ['Pending', 'Under Review', 'Planned', 'In Progress', 'Completed', 'Declined'];
const FR_STATUS_COLORS = {
  'Pending': '#E8E8E8', 'Under Review': '#FFE7B3', 'Planned': '#B3D4FF',
  'In Progress': '#D8B4E2', 'Completed': '#34C759', 'Declined': '#FF3B30',
};

export default function AdminPage() {
  const [activeTab, setActiveTab] = useState('teams');
  const [teams, setTeams] = useState([]);
  const [brands, setBrands] = useState([]);
  const [domains, setDomains] = useState([]);
  const [users, setUsers] = useState([]);
  const [notifications, setNotificationsState] = useState([]);
  const [featureRequests, setFeatureRequests] = useState([]);
  const [loading, setLoading] = useState(true);

  const [newName, setNewName] = useState('');
  const [newDesc, setNewDesc] = useState('');
  const [editId, setEditId] = useState(null);
  const [editName, setEditName] = useState('');

  // Notification form
  const [notifTitle, setNotifTitle] = useState('');
  const [notifDesc, setNotifDesc] = useState('');

  const loadData = useCallback(async () => {
    try {
      const [t, b, d, u, n, fr] = await Promise.all([
        getTeams(), getBrands(), getDomains(), getUsers(),
        getNotifications(), getFeatureRequests()
      ]);
      setTeams(t.data); setBrands(b.data); setDomains(d.data); setUsers(u.data);
      setNotificationsState(n.data); setFeatureRequests(fr.data);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const handleAdd = async () => {
    if (!newName.trim()) return;
    try {
      if (activeTab === 'teams') await createTeam({ name: newName, description: newDesc });
      else if (activeTab === 'brands') await createBrand({ name: newName, description: newDesc });
      else if (activeTab === 'domains') await createDomain({ name: newName, description: newDesc });
      setNewName(''); setNewDesc('');
      loadData();
    } catch (e) {
      if (e.response?.status === 403) alert('Control Panel access required');
      else console.error(e);
    }
  };

  const handleUpdate = async (id) => {
    if (!editName.trim()) return;
    try {
      if (activeTab === 'teams') await updateTeam(id, { name: editName, description: '' });
      else if (activeTab === 'brands') await updateBrand(id, { name: editName, description: '' });
      setEditId(null); setEditName('');
      loadData();
    } catch (e) { console.error(e); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure?')) return;
    try {
      if (activeTab === 'teams') await deleteTeam(id);
      else if (activeTab === 'brands') await deleteBrand(id);
      else if (activeTab === 'domains') await deleteDomain(id);
      loadData();
    } catch (e) { console.error(e); }
  };

  const handleRoleChange = async (uid, newRole) => {
    if (!window.confirm(`Change role to ${newRole}?`)) return;
    try { await changeUserRole(uid, newRole); loadData(); } catch (e) { console.error(e); }
  };

  const handleAddNotification = async () => {
    if (!notifTitle.trim()) return;
    try {
      await createNotification({ title: notifTitle, description: notifDesc });
      setNotifTitle(''); setNotifDesc('');
      loadData();
    } catch (e) { console.error(e); }
  };

  const handleDeleteNotification = async (id) => {
    if (!window.confirm('Delete notification?')) return;
    try { await deleteNotification(id); loadData(); } catch (e) { console.error(e); }
  };

  const handleFRStatusChange = async (id, status) => {
    try { await updateFeatureRequestStatus(id, status); loadData(); } catch (e) { console.error(e); }
  };

  const handleDeleteFR = async (id) => {
    if (!window.confirm('Delete feature request?')) return;
    try { await deleteFeatureRequest(id); loadData(); } catch (e) { console.error(e); }
  };

  const getItems = () => {
    if (activeTab === 'teams') return teams;
    if (activeTab === 'brands') return brands;
    if (activeTab === 'domains') return domains;
    return [];
  };

  if (loading) return <div className="p-8 text-[#555555]">Loading...</div>;

  return (
    <div className="p-6 lg:p-8" data-testid="admin-page">
      <div className="flex items-center gap-2 mb-1">
        <ShieldCheck size={22} weight="fill" className="text-[#32B4AC]" />
        <h1 className="font-['Manrope'] text-2xl font-bold tracking-tight text-[#0F0F0F]">Control Panel</h1>
      </div>
      <p className="text-sm text-[#555555] mb-6">Manage teams, brands, domains, users, notifications, and feature requests</p>

      {/* Tabs */}
      <div className="flex gap-0 border-b border-[#E8E8E8] mb-6 overflow-x-auto">
        {tabs.map(({ key, label, icon: Icon }) => (
          <button
            key={key}
            onClick={() => { setActiveTab(key); setEditId(null); }}
            className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${activeTab === key ? 'border-[#32B4AC] text-[#0F0F0F]' : 'border-transparent text-[#555555] hover:text-[#0F0F0F]'}`}
            data-testid={`admin-tab-${key}`}
          >
            <Icon size={16} />
            {label}
          </button>
        ))}
      </div>

      {/* ── Users Tab ── */}
      {activeTab === 'users' && (
        <div data-testid="admin-users-list">
          <p className="text-xs text-[#555555] mb-4">Manage user roles. Only admins can promote or demote users.</p>
          <div className="border border-[#E8E8E8] rounded-md overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-[#F4F4F4]">
                  <th className="text-left px-4 py-2.5 text-xs font-bold uppercase tracking-[0.15em] text-[#555555]">Name</th>
                  <th className="text-left px-3 py-2.5 text-xs font-bold uppercase tracking-[0.15em] text-[#555555]">Email</th>
                  <th className="text-left px-3 py-2.5 text-xs font-bold uppercase tracking-[0.15em] text-[#555555]">Role</th>
                  <th className="text-left px-3 py-2.5 text-xs font-bold uppercase tracking-[0.15em] text-[#555555]">Joined</th>
                  <th className="px-3 py-2.5 text-xs font-bold uppercase tracking-[0.15em] text-[#555555] text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map(u => {
                  const uid = u.user_id || u._id || u.id;
                  return (
                    <tr key={uid} className="border-t border-[#E8E8E8]" data-testid={`admin-user-${uid}`}>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          {u.picture ? <img src={u.picture} alt="" className="w-6 h-6 rounded-full" /> : <User size={16} className="text-[#555555]" />}
                          <span className="font-medium text-[#0F0F0F]">{u.name || '-'}</span>
                        </div>
                      </td>
                      <td className="px-3 py-3 text-[#555555]">{u.email}</td>
                      <td className="px-3 py-3">
                        <span className={`text-xs px-2.5 py-1 rounded-full font-semibold inline-flex items-center gap-1 ${u.role === 'admin' ? 'bg-[#32B4AC]/10 text-[#32B4AC]' : 'bg-[#E8E8E8] text-[#1A1A1A]'}`}>
                          {u.role === 'admin' && <ShieldCheck size={12} weight="fill" />}
                          {u.role || 'member'}
                        </span>
                      </td>
                      <td className="px-3 py-3 text-[#555555] text-xs">{u.created_at ? new Date(u.created_at).toLocaleDateString() : '-'}</td>
                      <td className="px-3 py-3 text-right">
                        {u.role === 'admin' ? (
                          <button onClick={() => handleRoleChange(uid, 'member')} className="text-xs px-3 py-1 border border-[#E8E8E8] rounded-md text-[#555555] hover:text-[#FF3B30] transition-colors" data-testid={`demote-user-${uid}`}>Remove Admin</button>
                        ) : (
                          <button onClick={() => handleRoleChange(uid, 'admin')} className="text-xs px-3 py-1 border border-[#32B4AC] rounded-md text-[#32B4AC] hover:bg-[#32B4AC]/5 transition-colors" data-testid={`promote-user-${uid}`}>Make Admin</button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ── Notifications Tab ── */}
      {activeTab === 'notifications' && (
        <div data-testid="admin-notifications-section">
          <div className="flex items-end gap-3 mb-6">
            <div className="flex-1 max-w-xs">
              <Label className="text-xs font-bold uppercase tracking-[0.2em] text-[#555555] mb-1.5 block">Title</Label>
              <Input data-testid="notif-title-input" value={notifTitle} onChange={e => setNotifTitle(e.target.value)} placeholder="Notification title" className="border-[#E8E8E8] rounded-md h-9" />
            </div>
            <div className="flex-1 max-w-sm">
              <Label className="text-xs font-bold uppercase tracking-[0.2em] text-[#555555] mb-1.5 block">Description</Label>
              <Input data-testid="notif-desc-input" value={notifDesc} onChange={e => setNotifDesc(e.target.value)} placeholder="Short description" className="border-[#E8E8E8] rounded-md h-9" />
            </div>
            <button data-testid="notif-add-btn" onClick={handleAddNotification} disabled={!notifTitle.trim()} className="h-9 px-4 bg-[#32B4AC] text-white text-sm font-semibold rounded-md hover:bg-[#2A9B94] disabled:opacity-50 flex items-center gap-1.5">
              <Plus size={14} weight="bold" /> Publish
            </button>
          </div>
          <div className="border border-[#E8E8E8] rounded-md overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-[#F4F4F4]">
                  <th className="text-left px-4 py-2.5 text-xs font-bold uppercase tracking-[0.15em] text-[#555555]">Title</th>
                  <th className="text-left px-3 py-2.5 text-xs font-bold uppercase tracking-[0.15em] text-[#555555]">Description</th>
                  <th className="text-left px-3 py-2.5 text-xs font-bold uppercase tracking-[0.15em] text-[#555555]">Published</th>
                  <th className="px-3 py-2.5 w-16"></th>
                </tr>
              </thead>
              <tbody>
                {notifications.map(n => (
                  <tr key={n.id} className="border-t border-[#E8E8E8]" data-testid={`admin-notif-${n.id}`}>
                    <td className="px-4 py-3 font-medium text-[#0F0F0F]">{n.title}</td>
                    <td className="px-3 py-3 text-[#555555] truncate max-w-[250px]">{n.description || '-'}</td>
                    <td className="px-3 py-3 text-xs text-[#555555]">{new Date(n.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</td>
                    <td className="px-3 py-3">
                      <button onClick={() => handleDeleteNotification(n.id)} className="p-1 hover:bg-red-50 rounded-md" data-testid={`delete-notif-${n.id}`}>
                        <Trash size={14} className="text-[#555555] hover:text-[#FF3B30]" />
                      </button>
                    </td>
                  </tr>
                ))}
                {notifications.length === 0 && <tr><td colSpan={4} className="text-center py-8 text-sm text-[#555555]">No notifications published yet</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ── Feature Requests Tab ── */}
      {activeTab === 'requests' && (
        <div data-testid="admin-feature-requests-section">
          <p className="text-xs text-[#555555] mb-4">Review and manage feature requests submitted by team members.</p>
          <div className="border border-[#E8E8E8] rounded-md overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-[#F4F4F4]">
                  <th className="text-left px-4 py-2.5 text-xs font-bold uppercase tracking-[0.15em] text-[#555555]">Title</th>
                  <th className="text-left px-3 py-2.5 text-xs font-bold uppercase tracking-[0.15em] text-[#555555]">Submitted By</th>
                  <th className="text-left px-3 py-2.5 text-xs font-bold uppercase tracking-[0.15em] text-[#555555]">Status</th>
                  <th className="text-left px-3 py-2.5 text-xs font-bold uppercase tracking-[0.15em] text-[#555555]">Date</th>
                  <th className="px-3 py-2.5 w-24"></th>
                </tr>
              </thead>
              <tbody>
                {featureRequests.map(fr => (
                  <tr key={fr.id} className="border-t border-[#E8E8E8]" data-testid={`admin-fr-${fr.id}`}>
                    <td className="px-4 py-3">
                      <p className="font-medium text-[#0F0F0F]">{fr.title}</p>
                      {fr.description && <p className="text-xs text-[#555555] mt-0.5 line-clamp-1">{fr.description}</p>}
                      {fr.use_case && <p className="text-[10px] text-[#555555] mt-0.5 italic line-clamp-1">{fr.use_case}</p>}
                    </td>
                    <td className="px-3 py-3 text-xs text-[#555555]">{fr.submitted_by_name}<br /><span className="text-[10px]">{fr.submitted_by_email}</span></td>
                    <td className="px-3 py-3">
                      <select
                        value={fr.status}
                        onChange={e => handleFRStatusChange(fr.id, e.target.value)}
                        className="h-7 text-xs border border-[#E8E8E8] rounded-md px-1.5 bg-white"
                        style={{ color: FR_STATUS_COLORS[fr.status] === '#E8E8E8' ? '#1A1A1A' : FR_STATUS_COLORS[fr.status] }}
                        data-testid={`fr-status-select-${fr.id}`}
                      >
                        {FR_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
                      </select>
                    </td>
                    <td className="px-3 py-3 text-xs text-[#555555]">{new Date(fr.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })}</td>
                    <td className="px-3 py-3">
                      <button onClick={() => handleDeleteFR(fr.id)} className="p-1 hover:bg-red-50 rounded-md" data-testid={`delete-fr-${fr.id}`}>
                        <Trash size={14} className="text-[#555555] hover:text-[#FF3B30]" />
                      </button>
                    </td>
                  </tr>
                ))}
                {featureRequests.length === 0 && <tr><td colSpan={5} className="text-center py-8 text-sm text-[#555555]">No feature requests yet</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ── Master Data Tabs (Teams/Brands/Domains) ── */}
      {['teams', 'brands', 'domains'].includes(activeTab) && (
        <>
          <div className="flex items-end gap-3 mb-6">
            <div className="flex-1 max-w-xs">
              <Label className="text-xs font-bold uppercase tracking-[0.2em] text-[#555555] mb-1.5 block">Name</Label>
              <Input data-testid="admin-add-name" value={newName} onChange={e => setNewName(e.target.value)} placeholder={`New ${activeTab.slice(0, -1)} name`} className="border-[#E8E8E8] rounded-md h-9" />
            </div>
            {activeTab !== 'domains' && (
              <div className="flex-1 max-w-xs">
                <Label className="text-xs font-bold uppercase tracking-[0.2em] text-[#555555] mb-1.5 block">Description</Label>
                <Input data-testid="admin-add-desc" value={newDesc} onChange={e => setNewDesc(e.target.value)} placeholder="Optional description" className="border-[#E8E8E8] rounded-md h-9" />
              </div>
            )}
            <button data-testid="admin-add-btn" onClick={handleAdd} disabled={!newName.trim()} className="h-9 px-4 bg-[#32B4AC] text-white text-sm font-semibold rounded-md hover:bg-[#2A9B94] disabled:opacity-50 flex items-center gap-1.5">
              <Plus size={14} weight="bold" /> Add
            </button>
          </div>
          <div className="border border-[#E8E8E8] rounded-md overflow-hidden" data-testid={`admin-${activeTab}-list`}>
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-[#F4F4F4]">
                  <th className="text-left px-4 py-2.5 text-xs font-bold uppercase tracking-[0.15em] text-[#555555]">Name</th>
                  {activeTab !== 'domains' && <th className="text-left px-3 py-2.5 text-xs font-bold uppercase tracking-[0.15em] text-[#555555]">Description</th>}
                  <th className="px-3 py-2.5 w-24"></th>
                </tr>
              </thead>
              <tbody>
                {getItems().map(item => (
                  <tr key={item.id} className="border-t border-[#E8E8E8]" data-testid={`admin-item-${item.id}`}>
                    <td className="px-4 py-3">
                      {editId === item.id ? (
                        <div className="flex gap-2">
                          <Input value={editName} onChange={e => setEditName(e.target.value)} className="h-7 text-sm border-[#E8E8E8] rounded-md" data-testid="admin-edit-name" />
                          <button onClick={() => handleUpdate(item.id)} className="text-xs text-[#32B4AC] font-medium hover:underline" data-testid="admin-save-edit">Save</button>
                          <button onClick={() => setEditId(null)} className="text-xs text-[#555555] hover:underline">Cancel</button>
                        </div>
                      ) : <span className="font-medium text-[#0F0F0F]">{item.name}</span>}
                    </td>
                    {activeTab !== 'domains' && <td className="px-3 py-3 text-[#555555]">{item.description || '-'}</td>}
                    <td className="px-3 py-3">
                      <div className="flex items-center gap-1 justify-end">
                        {activeTab !== 'domains' && (
                          <button onClick={() => { setEditId(item.id); setEditName(item.name); }} className="p-1 hover:bg-[#F4F4F4] rounded-md" data-testid={`admin-edit-${item.id}`}><Pencil size={14} className="text-[#555555]" /></button>
                        )}
                        <button onClick={() => handleDelete(item.id)} className="p-1 hover:bg-red-50 rounded-md" data-testid={`admin-delete-${item.id}`}><Trash size={14} className="text-[#555555] hover:text-[#FF3B30]" /></button>
                      </div>
                    </td>
                  </tr>
                ))}
                {getItems().length === 0 && <tr><td colSpan={3} className="text-center py-8 text-sm text-[#555555]">No items yet</td></tr>}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
